/**
 * reports-saturday.ts — Live Supabase helpers for the Saturday-build reports.
 *
 * One module per reporting concern keeps the existing reporting-live.ts stable.
 * All queries are tenant-scoped via getActiveTenantId(). No mock fallback —
 * empty results render as empty states in the consuming page.
 *
 * Tables read (all part of the saturday-build artifacts):
 *   public.deal_timing_records          (S-CP10-4)
 *   public.soft_stop_events             (RULE-ESC-1)
 *   public.soft_stop_audit              (RULE-ESC-1)
 *   public.tenant_escalation_config     (RULE-ESC-1)
 *   public.push_adequacy_queue          (TENANT-ADEQUACY-TOGGLE)
 *   public.deal_push_reentry            (RULE-PUSH-REENTRY)
 *   public.push_reentry_audit_log       (RULE-PUSH-REENTRY)
 *   public.users  (with external_consultant column — EXT-CONSULTANT-FLAG)
 *   public.accounts, public.tenants
 */
import { supabase, getSession } from "./session";

export const DEFAULT_TENANT_ID = 2;

function getActiveTenantId(): number {
  const s = getSession();
  return s?.tenantId ?? DEFAULT_TENANT_ID;
}

// ── Composite scoring + RULE-FORECAST-1 ────────────────────────────────────

// Canon 5-bucket taxonomy — CANON.md lines 167-186.
// Legacy "Push" and "Loss" both map to "Omit" at read-time.
export type ForecastCategory = "Commit" | "Upside" | "Best Case" | "Pipeline" | "Omit";
export type DeclaredTimingButton = "90" | "80" | "70" | "60" | "50" | "below_50";

/**
 * Maps the rep's declared-timing button to the forecast-category floor per
 * RULE-FORECAST-1. The floor is the HIGHEST category the deal is eligible for.
 */
export function categoryFloorForTiming(b: DeclaredTimingButton): ForecastCategory {
  switch (b) {
    case "90":
      return "Commit";
    case "80":
    case "70":
      return "Best Case";
    case "60":
    case "50":
      return "Pipeline";
    case "below_50":
      // Legacy: was "Push" — maps to Omit under canon 5-bucket taxonomy.
      return "Omit";
    default:
      return "Pipeline";
  }
}

/** Numeric % for declared button — 49 represents "Below 50". */
export function timingPctOf(b: DeclaredTimingButton): number {
  if (b === "below_50") return 49;
  return Number(b);
}

/**
 * Compute the displayed (blended) forecast %: composite × timing, rounded.
 * Returns 0 when either side is missing.
 */
export function displayedForecastPct(compositePct: number | null, timingPct: number | null): number {
  if (compositePct == null || timingPct == null) return 0;
  return Math.round((compositePct * timingPct) / 100);
}

export interface CompositeScoringRow {
  deal_id: string;
  account_id: number | null;
  account_name: string | null;
  ae_user_id: number | null;
  ae_name: string | null;
  composite_pct: number | null;
  declared_timing: DeclaredTimingButton | null;
  declared_timing_pct: number | null;
  displayed_forecast_pct: number;
  rule_forecast_1_category: ForecastCategory;
  category_eligibility_notes: string;
  soft_stop_flags: string[];
  manager_attention: boolean;
  close_date: string | null;
  updated_at: string | null;
}

/**
 * Lists per-deal composite scoring rows for the active tenant. Joins
 * deal_timing_records with the latest composite score from v_account_forecast.
 * Output is the shape the Composite Scoring report renders.
 */
export async function listCompositeScoringRows(): Promise<CompositeScoringRow[]> {
  const tenantId = getActiveTenantId();
  const sb = supabase();

  const { data: timing, error: e1 } = await sb
    .from("deal_timing_records")
    .select(
      "id,deal_id,close_date,rep_declared_timing_pct,system_band_lower,system_band_upper,soft_stop_flags,manager_override_applied,manager_override_pct,assessed_at",
    )
    .order("assessed_at", { ascending: false });
  if (e1) throw e1;

  const dealIds = Array.from(new Set((timing ?? []).map((t: any) => t.deal_id))).filter(Boolean);
  if (dealIds.length === 0) return [];

  const [{ data: forecasts }, { data: accounts }] = await Promise.all([
    sb.from("v_account_forecast").select("*").eq("tenant_id", tenantId).in("account_id", dealIds),
    sb.from("accounts").select("id,name,owner_user_id,tenant_id").eq("tenant_id", tenantId).in("id", dealIds),
  ]);

  const ownerIds = Array.from(new Set((accounts ?? []).map((a: any) => a.owner_user_id))).filter(Boolean);
  const { data: users } =
    ownerIds.length > 0
      ? await sb.from("users").select("id,full_name,email").in("id", ownerIds)
      : { data: [] as any[] };

  const fcByAcct = new Map<number, any>();
  for (const r of forecasts ?? []) fcByAcct.set(r.account_id, r);
  const acctById = new Map<number, any>();
  for (const a of accounts ?? []) acctById.set(a.id, a);
  const userById = new Map<number, any>();
  for (const u of users ?? []) userById.set(u.id, u);

  // Latest timing record per deal (timing rows are ordered desc above)
  const latestByDeal = new Map<string, any>();
  for (const t of timing ?? []) if (!latestByDeal.has(t.deal_id)) latestByDeal.set(t.deal_id, t);

  const rows: CompositeScoringRow[] = [];
  for (const [dealId, t] of latestByDeal) {
    const acct = acctById.get(dealId) ?? null;
    if (!acct) continue; // out-of-tenant, skip
    const fc = fcByAcct.get(dealId) ?? null;
    const owner = acct.owner_user_id != null ? userById.get(acct.owner_user_id) : null;

    const effectivePct = t.manager_override_applied ? t.manager_override_pct : t.rep_declared_timing_pct;
    const declared = effectivePct == null ? null : (numToButton(effectivePct) as DeclaredTimingButton);

    const compositePct = fc?.composite_score != null ? Number(fc.composite_score) : null;
    const timingPct = effectivePct == null ? null : Number(effectivePct);
    const displayed = displayedForecastPct(compositePct, timingPct);

    const category = declared ? categoryFloorForTiming(declared) : "Pipeline";
    const notes = declared === "50"
      ? "Pipeline only — manager attention flag"
      : declared === "below_50"
      ? "Omit — not in active pipeline (timing below 50)"
      : "Eligible";

    rows.push({
      deal_id: dealId,
      account_id: acct.id,
      account_name: acct.name ?? null,
      ae_user_id: acct.owner_user_id ?? null,
      ae_name: owner?.full_name ?? owner?.email ?? null,
      composite_pct: compositePct,
      declared_timing: declared,
      declared_timing_pct: timingPct,
      displayed_forecast_pct: displayed,
      rule_forecast_1_category: category,
      category_eligibility_notes: notes,
      soft_stop_flags: (t.soft_stop_flags as string[]) ?? [],
      manager_attention: declared === "50",
      close_date: t.close_date ?? null,
      updated_at: t.assessed_at ?? null,
    });
  }

  // Stable order: by AE name then by account
  rows.sort((a, b) => {
    const an = (a.ae_name ?? "").localeCompare(b.ae_name ?? "");
    if (an !== 0) return an;
    return (a.account_name ?? "").localeCompare(b.account_name ?? "");
  });
  return rows;
}

function numToButton(n: number): DeclaredTimingButton {
  if (n >= 90) return "90";
  if (n >= 80) return "80";
  if (n >= 70) return "70";
  if (n >= 60) return "60";
  if (n >= 50) return "50";
  return "below_50";
}

// ── Forecast roll-up ───────────────────────────────────────────────────────

export interface ForecastBucketRow {
  /** Canon bucket. "Pipeline+flag" = timing exactly 50. "Push/Loss-only" removed — use "Omit". */
  bucket: ForecastCategory | "Pipeline+flag";
  deal_count: number;
  total_weighted_value: number;
  total_displayed_value: number;
  deal_ids: number[];
}

/**
 * Roll up composite-scoring rows by RULE-FORECAST-1 category.
 * Pipeline+flag = declared_timing exactly 50.
 * Push/Loss-only = declared_timing below 50.
 */
export async function listForecastRollup(): Promise<ForecastBucketRow[]> {
  const rows = await listCompositeScoringRows();
  const tenantId = getActiveTenantId();
  const sb = supabase();

  // Need deal_value for weighting — join from v_account_forecast.
  const accountIds = rows.map((r) => r.account_id!).filter(Boolean);
  const { data: forecasts } =
    accountIds.length > 0
      ? await sb.from("v_account_forecast").select("account_id,deal_value,weighted_value").eq("tenant_id", tenantId).in("account_id", accountIds)
      : { data: [] as any[] };
  const fcByAcct = new Map<number, any>();
  for (const r of forecasts ?? []) fcByAcct.set(r.account_id, r);

  const buckets = new Map<string, ForecastBucketRow>();
  for (const r of rows) {
    let key: ForecastBucketRow["bucket"];
    // Legacy "below_50" timing — maps to Omit under canon.
    if (r.declared_timing === "below_50") key = "Omit";
    else if (r.manager_attention) key = "Pipeline+flag";
    else key = r.rule_forecast_1_category;

    const fc = r.account_id != null ? fcByAcct.get(r.account_id) : null;
    const dealValue = Number(fc?.deal_value ?? 0);
    const weighted = Number(fc?.weighted_value ?? 0);
    const displayed = Math.round((dealValue * r.displayed_forecast_pct) / 100);

    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { bucket: key, deal_count: 0, total_weighted_value: 0, total_displayed_value: 0, deal_ids: [] };
      buckets.set(key, bucket);
    }
    bucket.deal_count += 1;
    bucket.total_weighted_value += weighted;
    bucket.total_displayed_value += displayed;
    if (r.account_id != null) bucket.deal_ids.push(r.account_id);
  }

  // Stable display order
  // Canon 5-bucket order. Legacy "Push/Loss-only" bucket renamed to "Omit".
  // TODO: rename ForecastBucketRow.bucket union type to remove Push/Loss.
  const order: ForecastBucketRow["bucket"][] = ["Commit", "Upside", "Best Case", "Pipeline", "Pipeline+flag", "Omit"];
  return order.map((b) => buckets.get(b) ?? { bucket: b, deal_count: 0, total_weighted_value: 0, total_displayed_value: 0, deal_ids: [] });
}

// ── Escalation queue ───────────────────────────────────────────────────────

export interface EscalationRow {
  event_id: string;
  deal_id: number;
  account_name: string | null;
  lane_key: string;
  cp_number: number;
  question_key: string;
  assigned_manager_id: string;
  assigned_manager_name: string | null;
  created_at: string;
  escalation_deadline: string;
  hours_remaining: number;
  status: string;
  decision: string | null;
}

export async function listEscalationQueue(): Promise<EscalationRow[]> {
  const tenantId = getActiveTenantId();
  const sb = supabase();
  const { data, error } = await sb
    .from("soft_stop_events")
    .select("id,deal_id,lane_key,cp_number,question_key,assigned_manager_id,created_at,escalation_deadline,status,decision")
    .eq("tenant_id", tenantId)
    .order("escalation_deadline", { ascending: true });
  if (error) throw error;

  const dealIds = Array.from(new Set((data ?? []).map((r: any) => r.deal_id))).filter(Boolean);
  const mgrIds = Array.from(new Set((data ?? []).map((r: any) => r.assigned_manager_id))).filter(Boolean);

  const [{ data: accounts }, { data: mgrs }] = await Promise.all([
    dealIds.length > 0
      ? sb.from("accounts").select("id,name").in("id", dealIds)
      : Promise.resolve({ data: [] as any[] }),
    mgrIds.length > 0
      ? sb.from("users").select("id,full_name,email").in("id", mgrIds)
      : Promise.resolve({ data: [] as any[] }),
  ]);
  const acctById = new Map<number, any>();
  for (const a of accounts ?? []) acctById.set(a.id, a);
  const mgrById = new Map<string, any>();
  for (const m of mgrs ?? []) mgrById.set(String(m.id), m);

  const now = Date.now();
  return (data ?? []).map((r: any): EscalationRow => {
    const deadline = new Date(r.escalation_deadline).getTime();
    const hoursRemaining = Math.round((deadline - now) / 3_600_000);
    return {
      event_id: r.id,
      deal_id: r.deal_id,
      account_name: acctById.get(r.deal_id)?.name ?? null,
      lane_key: r.lane_key,
      cp_number: r.cp_number,
      question_key: r.question_key,
      assigned_manager_id: r.assigned_manager_id,
      assigned_manager_name:
        mgrById.get(String(r.assigned_manager_id))?.full_name ??
        mgrById.get(String(r.assigned_manager_id))?.email ??
        null,
      created_at: r.created_at,
      escalation_deadline: r.escalation_deadline,
      hours_remaining: hoursRemaining,
      status: r.status,
      decision: r.decision ?? null,
    };
  });
}

// ── PUSH adequacy ──────────────────────────────────────────────────────────

export interface AdequacyConfigRow {
  tenant_id: number;
  adequacy_check_mode: "rep_self_check" | "manager_judges";
  escalation_contact_name: string | null;
  escalation_contact_email: string | null;
}

export interface AdequacyStatsRow {
  tenant_id: number;
  total_submitted: number;
  pending: number;
  adequate: number;
  inadequate: number;
  auto_loss: number;
  retries: number;
  auto_loss_reasons: { reason: string; count: number }[];
}

export async function listAdequacyConfig(): Promise<AdequacyConfigRow[]> {
  const tenantId = getActiveTenantId();
  const sb = supabase();
  const { data, error } = await sb
    .from("tenant_escalation_config")
    .select("tenant_id,adequacy_check_mode,escalation_contact_name,escalation_contact_email")
    .eq("tenant_id", tenantId);
  if (error) throw error;
  return (data ?? []) as AdequacyConfigRow[];
}

export async function listAdequacyStats(): Promise<AdequacyStatsRow> {
  const tenantId = getActiveTenantId();
  const sb = supabase();
  const { data, error } = await sb
    .from("push_adequacy_queue")
    .select("status,is_retry,auto_loss_reason")
    .eq("tenant_id", tenantId);
  if (error) throw error;

  const reasons = new Map<string, number>();
  let pending = 0, adequate = 0, inadequate = 0, auto_loss = 0, retries = 0;
  for (const r of data ?? []) {
    if (r.status === "pending") pending += 1;
    if (r.status === "adequate") adequate += 1;
    if (r.status === "inadequate") inadequate += 1;
    if (r.status === "auto_loss") {
      auto_loss += 1;
      const k = (r.auto_loss_reason ?? "unspecified") as string;
      reasons.set(k, (reasons.get(k) ?? 0) + 1);
    }
    if (r.is_retry) retries += 1;
  }
  return {
    tenant_id: tenantId,
    total_submitted: data?.length ?? 0,
    pending,
    adequate,
    inadequate,
    auto_loss,
    retries,
    auto_loss_reasons: Array.from(reasons.entries()).map(([reason, count]) => ({ reason, count })),
  };
}

// ── PUSH re-entry ──────────────────────────────────────────────────────────

export interface PushReentryRow {
  deal_id: number;
  account_id: number;
  account_name: string | null;
  reentered_in_quarter: string | null;
  reentered_at: string | null;
  all_forced_cps_cleared: boolean;
  revalidated_cps: number[];
  retained_cps: number[];
  quarter_rollover_triggered: boolean;
}

export async function listPushReentryRows(): Promise<PushReentryRow[]> {
  const tenantId = getActiveTenantId();
  const sb = supabase();
  const { data, error } = await sb
    .from("deal_push_reentry")
    .select("deal_id,account_id,reentry_state,updated_at");
  if (error) throw error;

  const accountIds = Array.from(new Set((data ?? []).map((r: any) => r.account_id)));
  const { data: accounts } =
    accountIds.length > 0
      ? await sb.from("accounts").select("id,name,tenant_id").eq("tenant_id", tenantId).in("id", accountIds)
      : { data: [] as any[] };
  const acctById = new Map<number, any>();
  for (const a of accounts ?? []) acctById.set(a.id, a);

  // Quarter-rollover trigger: detected from audit log events of type PUSH_REENTRY_TRIGGERED
  const dealIds = (data ?? []).map((r: any) => r.deal_id);
  const { data: audit } =
    dealIds.length > 0
      ? await sb.from("push_reentry_audit_log").select("deal_id,event_type").in("deal_id", dealIds)
      : { data: [] as any[] };
  const triggered = new Set<number>();
  for (const r of audit ?? []) if (r.event_type === "PUSH_REENTRY_TRIGGERED") triggered.add(r.deal_id);

  const out: PushReentryRow[] = [];
  for (const r of data ?? []) {
    const acct = acctById.get(r.account_id);
    if (!acct) continue;
    const st = (r.reentry_state ?? {}) as any;
    const flags = (st.checkpointFlags ?? {}) as Record<string, any>;
    const revalidated: number[] = [];
    const retained: number[] = [];
    for (const [cp, info] of Object.entries(flags)) {
      const cpNum = Number(cp);
      if (!Number.isFinite(cpNum)) continue;
      const i = info as any;
      if (i?.cleared) revalidated.push(cpNum);
      if (i?.retained && !i?.cleared) retained.push(cpNum);
    }
    revalidated.sort((a, b) => a - b);
    retained.sort((a, b) => a - b);
    out.push({
      deal_id: r.deal_id,
      account_id: r.account_id,
      account_name: acct.name ?? null,
      reentered_in_quarter: st.reenteredInQuarter ?? null,
      reentered_at: st.reenteredAt ?? null,
      all_forced_cps_cleared: !!st.allForcedCpsCleared,
      revalidated_cps: revalidated,
      retained_cps: retained,
      quarter_rollover_triggered: triggered.has(r.deal_id),
    });
  }
  return out;
}

// ── External consultants (admin) ───────────────────────────────────────────

export interface ExternalConsultantRow {
  user_id: number;
  full_name: string | null;
  email: string;
  role: string;
  tenant_id: number;
  tenant_name: string | null;
}

/** Admin-only — caller is responsible for gating by role/email. */
export async function listExternalConsultants(): Promise<ExternalConsultantRow[]> {
  const sb = supabase();
  const { data: users, error } = await sb
    .from("users")
    .select("id,full_name,email,role,tenant_id,external_consultant")
    .eq("external_consultant", true);
  if (error) throw error;

  const tenantIds = Array.from(new Set((users ?? []).map((u: any) => u.tenant_id)));
  const { data: tenants } =
    tenantIds.length > 0
      ? await sb.from("tenants").select("id,name").in("id", tenantIds)
      : { data: [] as any[] };
  const tById = new Map<number, any>();
  for (const t of tenants ?? []) tById.set(t.id, t);

  return (users ?? []).map((u: any): ExternalConsultantRow => ({
    user_id: u.id,
    full_name: u.full_name ?? null,
    email: u.email,
    role: u.role,
    tenant_id: u.tenant_id,
    tenant_name: tById.get(u.tenant_id)?.name ?? null,
  }));
}

// ── SOFT_STOP audit trail (RULE-ESC-1) ─────────────────────────────────────

export interface SoftStopAuditRow {
  audit_id: string;
  event_id: string;
  deal_id: number;
  account_name: string | null;
  lane_key: string;
  cp_number: number;
  question_key: string;
  action: string;
  actor_id: string | null;
  actor_name: string | null;
  actor_role: string | null;
  reason: string | null;
  occurred_at: string;
}

export async function listSoftStopAudit(): Promise<SoftStopAuditRow[]> {
  const tenantId = getActiveTenantId();
  const sb = supabase();

  // Audit is keyed on event_id; join through soft_stop_events to filter by tenant.
  const { data: events, error: e1 } = await sb
    .from("soft_stop_events")
    .select("id,deal_id,lane_key,cp_number,question_key")
    .eq("tenant_id", tenantId);
  if (e1) throw e1;
  if (!events?.length) return [];

  const eventIds = events.map((e: any) => e.id);
  const evtById = new Map<string, any>();
  for (const e of events) evtById.set(e.id, e);

  const { data: audit, error: e2 } = await sb
    .from("soft_stop_audit")
    .select("id,event_id,action,actor_id,actor_role,reason,occurred_at")
    .in("event_id", eventIds)
    .order("occurred_at", { ascending: false });
  if (e2) throw e2;

  const dealIds = Array.from(new Set(events.map((e: any) => e.deal_id)));
  const actorIds = Array.from(new Set((audit ?? []).map((a: any) => a.actor_id).filter(Boolean)));
  const [{ data: accounts }, { data: users }] = await Promise.all([
    dealIds.length > 0
      ? sb.from("accounts").select("id,name").in("id", dealIds)
      : Promise.resolve({ data: [] as any[] }),
    actorIds.length > 0
      ? sb.from("users").select("id,full_name,email").in("id", actorIds)
      : Promise.resolve({ data: [] as any[] }),
  ]);
  const acctById = new Map<number, any>();
  for (const a of accounts ?? []) acctById.set(a.id, a);
  const userById = new Map<string, any>();
  for (const u of users ?? []) userById.set(String(u.id), u);

  return (audit ?? []).map((a: any): SoftStopAuditRow => {
    const evt = evtById.get(a.event_id) ?? {};
    return {
      audit_id: a.id,
      event_id: a.event_id,
      deal_id: evt.deal_id,
      account_name: acctById.get(evt.deal_id)?.name ?? null,
      lane_key: evt.lane_key,
      cp_number: evt.cp_number,
      question_key: evt.question_key,
      action: a.action,
      actor_id: a.actor_id ?? null,
      actor_name:
        userById.get(String(a.actor_id))?.full_name ??
        userById.get(String(a.actor_id))?.email ??
        null,
      actor_role: a.actor_role ?? null,
      reason: a.reason ?? null,
      occurred_at: a.occurred_at,
    };
  });
}

// ── CSV helper ─────────────────────────────────────────────────────────────

export function toCsv(rows: Record<string, any>[], columns?: string[]): string {
  if (!rows.length) return "";
  const cols = columns ?? Object.keys(rows[0]);
  const esc = (v: any): string => {
    if (v == null) return "";
    const s = Array.isArray(v) ? v.join("|") : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = cols.join(",");
  const body = rows.map((r) => cols.map((c) => esc(r[c])).join(",")).join("\n");
  return `${header}\n${body}\n`;
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
